import React, { useState } from 'react';
import ModalPopupComponent from '@src/CommonComponents/ModalPopupComponent/ModalPopupComponent';
import { getImpactRedirectUrl } from '@src/middleware/services/pnb/impactRedirect.service';
import { fetchGenesisIdFromUHCI } from '@src/apps/ProviderBriefing/ProviderBriefingDocument/Main/main.service';
import { config } from '@uhg-abyss/web/tools/config';
import {
  IMPACT_STORAGE_KEY,
  IMPACT_MODAL_TYPE,
  IMPACT_MODAL_TITLE_NAME,
  IMPACT_MODAL_TITLE,
  IMPACT_MODAL_COLOR,
  IMPACT_MODAL_CANCEL_TEXT,
  IMPACT_MODAL_CONFIRM_TEXT,
  IMPACT_MSG_SUCCESS,
  IMPACT_MSG_PROVIDER_NOT_FOUND,
  IMPACT_MSG_TIN_NOT_FOUND,
} from './constants';

// NavigationBar context: BicId from providerInfo, fallback to multiSelectedTins
const getNavigationParams = (): { genesisId?: string; tin?: string } => {
  try {
    const providerInfoStr = sessionStorage.getItem('providerInfo');
    if (providerInfoStr) {
      const providerInfo = JSON.parse(providerInfoStr);
      if (providerInfo?.BicId) {
        return { genesisId: String(providerInfo.BicId) };
      }
    }
  } catch { /* ignore */ }

  const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');
  const tin = multiSelectedTins ? multiSelectedTins.split(',')[0] : null;
  return tin ? { tin } : {};
};

// Provider Briefing context: always resolves genesis ID (never sends TIN).
// Falls back to UHCI details API when genesis ID is not in session storage.
const getPNBParams = async (): Promise<{ genesisId?: string }> => {
  try {
    const pnbDataStr = sessionStorage.getItem('PNBData');
    if (pnbDataStr) {
      const pnbData = JSON.parse(pnbDataStr);
      if (pnbData?.gensisId) {
        return { genesisId: String(pnbData.gensisId) };
      }
    }
  } catch { /* ignore */ }

  // Genesis ID not in session storage — fetch from UHCI details API
  try {
    const genesisId = await fetchGenesisIdFromUHCI();
    if (genesisId) {
      return { genesisId };
    }
  } catch { /* ignore */ }
  return {};
};

export type ImpactNavigationSource = 'navigation' | 'pnb';

export function useImpactNavigation(source: ImpactNavigationSource = 'navigation') {
  const [modalDetail, setModalDetail] = useState<{ isOpen: boolean; data: any }>({
    isOpen: false,
    data: {},
  });
  const [isLoading, setIsLoading] = useState(false);

  const navigateImpact = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const params:any = source === 'pnb' ? await getPNBParams() : getNavigationParams();

      // Skip the redirect API call when there's nothing to send — avoids 400 "Either genesisId or tin is required".
      // PNB: skip when genesis ID could not be resolved.
      // Navigation: skip when sessionStorage has neither BicId (providerInfo) nor a TIN (multiSelectedTins).
      const skipRedirectCall =
        (source === 'pnb' && !params.genesisId) ||
        (source === 'navigation' && !params.genesisId && !params.tin);
      const response = skipRedirectCall ? null : await getImpactRedirectUrl(params);
      const redirectData = response?.data;

      const isSuccess = !skipRedirectCall && redirectData?.status?.toUpperCase() === 'SUCCESS' && !!redirectData?.url;

      let redirectUrl: string;
      let message: string;

      if (isSuccess) {
        redirectUrl = redirectData.url;
        message = IMPACT_MSG_SUCCESS;
      } else {
        const fallbackUrl = config('IMPACT_URL');
        if (!fallbackUrl) {
          return;
        }
        redirectUrl = fallbackUrl;
        // PNB source always resolves via genesis ID — show provider-not-found, never TIN-not-found.
        // Navigation source: if we had a genesis ID (BicId) show provider-not-found, else TIN-not-found.
        // When skipRedirectCall=true for navigation (empty params), show provider-not-found as generic fallback.
        message = (source === 'pnb' || params.genesisId || skipRedirectCall)
          ? IMPACT_MSG_PROVIDER_NOT_FOUND
          : IMPACT_MSG_TIN_NOT_FOUND;
      }

      const options = {
        type: IMPACT_MODAL_TYPE,
        titleName: `${IMPACT_MODAL_TITLE_NAME} ${IMPACT_MODAL_TITLE}`,
        title: '',
        color: IMPACT_MODAL_COLOR,
        message,
        checked: false,
        dontShowText: '',
        cancelText: IMPACT_MODAL_CANCEL_TEXT,
        confirmText: IMPACT_MODAL_CONFIRM_TEXT,
        storageKey: IMPACT_STORAGE_KEY,
        redirectUrl,
      };
      setModalDetail({ isOpen: true, data: options });
    } catch {
      // silently handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => setModalDetail({ isOpen: false, data: {} });

  const handleContinue = (checkBoxValue: boolean, data: any) => {
    // "Don't show again" is disabled; checkbox value intentionally not persisted
    setModalDetail({ isOpen: false, data: {} });
    window.open(data?.redirectUrl, '_blank');
  };

  const ImpactModal = () =>
    React.createElement(ModalPopupComponent, {
      show: modalDetail?.isOpen,
      data: modalDetail?.data,
      onClose: handleClose,
      onContinue: handleContinue,
    });

  return { navigateImpact, ImpactModal };
}
